/**
 * eSalon Consultation — step access gate (DEV-4285)
 *
 * Steps 2 and 3 are only meaningful to someone who finished step 1. Reached
 * directly — a shared link, a bookmark, a new tab, a cleared session — they
 * would otherwise render a routine derived from NO answers: `recommend({})`
 * still returns products, so the page looks personalised while carrying an
 * empty profile, and Continue would put that on a real order.
 *
 * Completion is an explicit marker written by <consultation-form> on submit,
 * not "are there any stored answers". The quiz persists on every keystroke, so
 * the answers key exists as soon as someone touches the first question; only
 * the marker means they got to the end and passed validation.
 *
 * sessionStorage is the right scope: it is per-tab, so a link opened in a new
 * tab correctly fails the gate even though the same browser finished the quiz
 * a moment ago in another one.
 */
(function () {
  const host = window;
  host.esalonConsultation = host.esalonConsultation || {};

  const key = (profile) => `esalon:consultation:${profile}:complete`;

  /**
   * Set when a write did not survive. Quota-exceeded and legacy Safari private
   * mode are the asymmetric case the obvious implementation gets wrong:
   * `setItem` throws while `getItem` keeps returning null, so a read-only
   * fail-open still bounces the customer. Without this they finish the quiz, get
   * sent back to step 1, finish it again, and are sent back again — a loop with
   * no way out.
   */
  let storageBroken = false;

  /**
   * Probed at load, on EVERY page, not inferred from a failed write earlier in
   * the flow. The flag is per-document: step 1 catching a failed write tells
   * steps 2 and 3 nothing, so without this probe a shopper whose storage
   * rejects writes finishes the quiz, is bounced back to step 1, finishes it
   * again, and is bounced again — with no way out.
   *
   * A write probe rather than a read: the failure being guarded against is
   * storage that reads fine and silently refuses to keep anything (quota
   * exhaustion, and Safari's legacy private mode), as well as access throwing
   * outright (SecurityError when a browser blocks all site data).
   */
  (function probeStorage() {
    const probe = 'esalon:storage-probe';
    try {
      sessionStorage.setItem(probe, '1');
      if (sessionStorage.getItem(probe) !== '1') storageBroken = true;
      sessionStorage.removeItem(probe);
    } catch {
      storageBroken = true;
    }
  })();

  function write(name, value) {
    try {
      sessionStorage.setItem(name, value);
      // Read back: a throw is not the only way a write fails to stick.
      if (sessionStorage.getItem(name) !== value) storageBroken = true;
    } catch {
      storageBroken = true;
    }
  }

  function read(name) {
    try {
      return sessionStorage.getItem(name);
    } catch {
      storageBroken = true;
      return null;
    }
  }

  function drop(name) {
    try {
      sessionStorage.removeItem(name);
    } catch {
      /* nothing to do */
    }
  }

  host.esalonConsultation.markComplete = function markComplete(profile) {
    write(key(profile), '1');

    // A new run of the consultation invalidates the previous one. Without this
    // the completion marker would mean "finished once, ever", and a customer who
    // went back and changed their answers would be shown the OLD stored
    // recommendation while their NEW answers went onto the line items.
    //
    // The cart is NOT reconciled here — that happens on the routine step, which
    // replaces whatever a previous run left behind (removePreviousRoutine).
    drop(`esalon:recommendation:${profile}`);
  };

  host.esalonConsultation.isComplete = function isComplete(profile) {
    const value = read(key(profile));

    // Checked AFTER the read, not before. `read()` is what discovers that access
    // throws, and the gate only ever calls this once — testing the flag first
    // meant the very call that matters returned false and redirected, and only a
    // second call within the same document would have failed open.
    //
    // Fail open: being unable to PROVE completion must never lock out someone
    // who did complete it.
    if (storageBroken) return true;
    return value === '1';
  };

  host.esalonConsultation.clearComplete = function clearComplete(profile) {
    drop(key(profile));
  };

  /**
   * The one place this module leaves the page. `replace`, not `assign`: the
   * incomplete step must not become a history entry, or Back from step 1 lands
   * the customer straight back on it. Isolated as its own property because
   * jsdom's Location is neither writable nor configurable, so this is the only
   * form of redirect a test can observe.
   */
  host.esalonConsultation.redirect = function redirect(url) {
    window.location.replace(url);
  };

  /**
   * Returns true when the caller should STOP setting up — a redirect is under
   * way.
   */
  host.esalonConsultation.requireComplete = function requireComplete(profile, redirectUrl) {
    if (!redirectUrl) return false;
    if (host.esalonConsultation.isComplete(profile)) return false;

    host.esalonConsultation.redirect(redirectUrl);
    return true;
  };
})();
